/**
 * usePreviewImage — fetches the REDACTED derivative of a photo from
 * GET /v1/photos/:id/preview (owner-authed).
 *
 * Returns 404 until the derivative render is complete. Used by the
 * publish-confirm modal to show the owner exactly what the public will see.
 *
 * Mirrors usePhotoImage but targets the /preview endpoint (blurred derivative)
 * instead of /raw.
 */

import { env } from '@/lib/env';
import { auth } from '@/lib/firebase';
import { useEffect, useState } from 'react';

export type PreviewImageState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; url: string }
  | { status: 'not_found' }
  | { status: 'error'; message: string };

export function usePreviewImage(
  photoId: string | null | undefined,
  enabled = true,
): PreviewImageState {
  const [state, setState] = useState<PreviewImageState>({ status: 'idle' });

  useEffect(() => {
    if (!photoId || !enabled) {
      setState({ status: 'idle' });
      return;
    }

    let cancelled = false;
    let objectUrl: string | null = null;

    setState({ status: 'loading' });

    async function fetchPreview() {
      const user = auth.currentUser;
      if (!user) {
        if (!cancelled) setState({ status: 'error', message: 'Not signed in' });
        return;
      }

      const token = await user.getIdToken();
      const endpoint = `${env.VITE_GG_API_BASE_URL}/v1/photos/${encodeURIComponent(photoId!)}/preview`;

      let res: Response;
      try {
        res = await fetch(endpoint, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch {
        if (!cancelled) setState({ status: 'error', message: 'Network error fetching preview' });
        return;
      }

      if (res.status === 404) {
        if (!cancelled) setState({ status: 'not_found' });
        return;
      }

      if (!res.ok) {
        if (!cancelled)
          setState({ status: 'error', message: `Could not load preview (${res.status})` });
        return;
      }

      let blob: Blob;
      try {
        blob = await res.blob();
      } catch {
        if (!cancelled) setState({ status: 'error', message: 'Could not read preview data' });
        return;
      }

      objectUrl = URL.createObjectURL(blob);
      if (!cancelled) {
        setState({ status: 'ready', url: objectUrl });
      } else {
        URL.revokeObjectURL(objectUrl);
        objectUrl = null;
      }
    }

    void fetchPreview();

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        objectUrl = null;
      }
    };
  }, [photoId, enabled]);

  return state;
}
